import React, { useMemo, useState } from "react";
import { Group } from "@visx/group";
import {
  Treemap,
  treemapSquarify,
  treemapBinary,
  treemapSliceDice,
} from "@visx/hierarchy";
import { Tooltip, TooltipWithBounds, defaultStyles } from "@visx/tooltip";
import { Text } from "@visx/text";
import { hierarchy } from "d3-hierarchy";
import { useChartDimensions } from "../../../../hooks/useChartDimensions";

// Helper to provide a color for each node
const getNodeColor = (node, colors, depth = 0, index = 0) => {
  if (node.data.color) {
    return node.data.color;
  }

  // Use the index to pick a color from the array, cycling through if needed
  return colors[index % colors.length];
};

const TreeMap = ({
  data,
  width = 600,
  height = 400,
  margin = { top: 10, right: 10, bottom: 10, left: 10 },
  colors = [
    "#1a515f", // mblue-900
    "#1a6170", // mblue-800
    "#157787", // mblue-700
    "#1296a8", // mblue-600
    "#12bcc8", // mblue-500
    "#2dd9e3", // mblue-400
    "#6fecf1", // mblue-300
    "#aaf5f7", // mblue-200
    "#d2fbfb", // mblue-100
    "#edfefe", // mblue-50
  ],
  animate = true,
  className = "",
  title = undefined,
  algorithm = "squarify",
  minNodeSizeToDisplayLabel = 12,
  showTooltip = true,
  onNodeClick,
  maxNodes = 10,
  selectedCategory = null,
  questionId = null,
}) => {
  // Track tooltip state
  const [tooltipData, setTooltipData] = useState(null);

  // Process data to limit number of nodes
  const processedData = useMemo(() => {
    if (!data || !data.children) return data;

    // Sort children by value and take top N
    const sortedChildren = [...data.children].sort(
      (a, b) => (b.value || 0) - (a.value || 0)
    );
    const topChildren = sortedChildren.slice(0, maxNodes);

    // If there are more items, add an "Other" category
    if (sortedChildren.length > maxNodes) {
      const otherValue = sortedChildren
        .slice(maxNodes)
        .reduce((sum, item) => sum + (item.value || 0), 0);

      topChildren.push({
        name: "Other",
        value: otherValue,
      });
    }

    return {
      ...data,
      children: topChildren,
    };
  }, [data, maxNodes]);

  const [containerRef, dimensions] = useChartDimensions({
    width,
    height,
    marginTop: margin.top,
    marginRight: margin.right,
    marginBottom: margin.bottom,
    marginLeft: margin.left,
  });

  const { boundedWidth, boundedHeight, margin: chartMargin } = dimensions;

  // Select the right algorithm based on props
  const tileMethod = useMemo(() => {
    switch (algorithm) {
      case "binary":
        return treemapBinary;
      case "sliceDice":
        return treemapSliceDice;
      case "squarify":
      default:
        return treemapSquarify;
    }
  }, [algorithm]);

  // Create hierarchical structure using d3-hierarchy
  const root = useMemo(() => {
    return hierarchy(processedData)
      .sum((d) => d.value || 0)
      .sort((a, b) => (b.value || 0) - (a.value || 0));
  }, [processedData]);

  // Helper function to format value for tooltip
  const formatValue = (value) => {
    return value.toLocaleString();
  };

  // Calculate total value for percentage calculation
  const totalValue = useMemo(() => root.value || 0, [root]);

  // Helper function to format percentage
  const formatPercentage = (value) => {
    const percentage = (value / totalValue) * 100;
    return `${percentage.toFixed(0)}%`;
  };

  return (
    <div>
      {title && <div className="title">{title}</div>}
      <div ref={containerRef} className={className}>
        <svg width={width} height={height}>
          <Group top={chartMargin?.top} left={chartMargin?.left}>
            <Treemap
              root={root}
              size={[boundedWidth, boundedHeight]}
              tile={tileMethod}
              round={true}
            >
              {(treemap) => {
                const nodes = treemap.descendants(); // Get all nodes including root
                return (
                  <Group>
                    {nodes
                      .filter((node) => node.depth > 0)
                      .map((node, i) => {
                        const width = node.x1 - node.x0;
                        const height = node.y1 - node.y0;
                        const depth = node.depth;

                        // Calculate font size based on node size relative to total size
                        const nodeArea = width * height;
                        const totalArea = boundedWidth * boundedHeight;
                        const sizeRatio = Math.sqrt(nodeArea / totalArea);

                        // Scale font size between 16 and 32 based on node size
                        const fontSize = Math.max(
                          16,
                          Math.min(32, 32 * sizeRatio)
                        );
                        const percentageFontSize = Math.max(fontSize * 0.8, 14);

                        const shouldShowLabel =
                          Math.min(width, height) > minNodeSizeToDisplayLabel;
                        // Determine if this node is selected globally
                        const isNodeSelected =
                          selectedCategory &&
                          selectedCategory.questionId === questionId &&
                          selectedCategory.category === node.data.name;
                        const isAnyNodeSelectedInThisTreemap =
                          selectedCategory &&
                          selectedCategory.questionId === questionId;
                        const nodeOpacity = isNodeSelected
                          ? 1
                          : isAnyNodeSelectedInThisTreemap
                            ? 0.5
                            : 1;
                        return (
                          <Group key={`node-${i}`} top={node.y0} left={node.x0}>
                            <rect
                              width={width}
                              height={height}
                              fill={getNodeColor(node, colors, depth, i)}
                              stroke={
                                isNodeSelected
                                  ? "#ffffff"
                                  : "rgba(255,255,255,0.2)"
                              }
                              strokeWidth={isNodeSelected ? 4 : 1}
                              style={{
                                cursor: onNodeClick ? "pointer" : "default",
                                filter: isNodeSelected
                                  ? "drop-shadow(0 0 6px rgba(255,255,255,0.8)) brightness(0.85)"
                                  : "none",
                                transition: "all 0.2s ease-in-out",
                                opacity: nodeOpacity,
                              }}
                              onClick={() => {
                                onNodeClick && onNodeClick(node.data);
                              }}
                              onMouseMove={(event) => {
                                if (showTooltip) {
                                  const coords = {
                                    x: event.clientX,
                                    y: event.clientY,
                                  };
                                  setTooltipData({
                                    name: node.data.name,
                                    value: node.value || 0,
                                    x: coords.x,
                                    y: coords.y,
                                  });
                                }
                              }}
                              onMouseLeave={() => setTooltipData(null)}
                            />
                            {shouldShowLabel && (
                              <>
                                <Text
                                  x={width / 2}
                                  y={height / 2 - fontSize / 2}
                                  width={width - width * 0.3}
                                  verticalAnchor="end"
                                  textAnchor="middle"
                                  fill="#ffffff"
                                  fontSize={fontSize}
                                  fontFamily="Sofia Sans Extra Condensed"
                                  className="treemap-label"
                                  style={{
                                    pointerEvents: "none",
                                    userSelect: "none",
                                  }}
                                >
                                  {node.data.name}
                                </Text>
                                <Text
                                  x={width / 2}
                                  y={height / 2 + fontSize / 2}
                                  width={width - width * 0.3}
                                  verticalAnchor="start"
                                  textAnchor="middle"
                                  fill="#ffffff"
                                  fontSize={percentageFontSize}
                                  fontFamily="Sofia Sans Extra Condensed"
                                  style={{
                                    pointerEvents: "none",
                                    userSelect: "none",
                                  }}
                                >
                                  {formatPercentage(node.value || 0)}
                                </Text>
                              </>
                            )}
                          </Group>
                        );
                      })}
                  </Group>
                );
              }}
            </Treemap>
          </Group>
        </svg>
        {tooltipData && (
          <TooltipWithBounds
            key={Math.random()}
            top={tooltipData.y}
            left={tooltipData.x}
            style={{
              ...defaultStyles,
              backgroundColor: "white",
              color: "#333333",
              padding: "0.5rem",
              boxShadow:
                "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
              borderRadius: "4px",
            }}
          >
            <div>
              <strong>{tooltipData.name}</strong>
              <div>
                {formatValue(tooltipData.value)} (
                {formatPercentage(tooltipData.value)})
              </div>
            </div>
          </TooltipWithBounds>
        )}
      </div>
    </div>
  );
};

export default TreeMap;
